import React from 'react'
import PropTypes from 'prop-types'
import {useIntl, FormattedMessage} from 'react-intl'
import {
    Text,
    Stack,
    Divider,
    Alert,
    Link as RegularLink
} from '@salesforce/retail-react-app/app/components/shared/ui'
import Link from '@salesforce/retail-react-app/app/components/link'
import {ORDER_RETURN_RAW_STATUS, RETURNABLE_WINDOW_EXCEEDED_REASON, RETURN_LABEL_URL} from '../constant'

export const ReturnOrderPolicyAlert = ({returnable, containerProps, reason}) => {
    const {formatMessage} = useIntl()

    if (!returnable && reason === RETURNABLE_WINDOW_EXCEEDED_REASON) {
        return (
            <Alert variant="warning" {...containerProps}>
                <Text>
                    {formatMessage(
                        {
                            defaultMessage:
                                'Eligible return window has exceeded. {returnPolicyLink}',
                            id: 'account_order_detail.label.return.exceeded'
                        },
                        {
                            returnPolicyLink: (
                                <Link
                                    href="/return-policy.html"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    textDecoration="underline"
                                >
                                    <FormattedMessage
                                        id="account_order_detail.label.return.see.policy"
                                        defaultMessage="See Return Policy"
                                    />
                                </Link>
                            )
                        }
                    )}
                </Text>
            </Alert>
        )
    }
}

ReturnOrderPolicyAlert.propTypes = {
    returnable: PropTypes.bool,
    reason: PropTypes.string,
    orderStatus: PropTypes.string,
    containerProps: PropTypes.object
}

export const ManageOrder = ({returnable, returnLabel, containerProps, dividerProps}) => {
    const handlePrintLabel = (e) => {
        e.preventDefault()

        // If no returnLabel URL return to external URL
        if (!returnLabel) {
            window.open(RETURN_LABEL_URL, '_blank')
            return
        }

        // Try to open in new tab
        const newWindow = window.open(returnLabel, '_blank')

        // If popup blocked or failed, return to external URL
        if (!newWindow) {
            window.open(RETURN_LABEL_URL, '_blank')
        }
    }

    if (returnable) {
        return (
            <Stack alignItems="flex-start" gap={3} {...containerProps}>
                <Text fontSize="md" fontWeight={700} textTransform="uppercase" lineHeight="20px">
                    <FormattedMessage
                        defaultMessage="Manage Your Order"
                        id="account_order_detail.label.manage_your_order"
                    />
                </Text>
                <RegularLink
                    as="button"
                    onClick={handlePrintLabel}
                    textDecoration="underline"
                    fontSize="sm"
                >
                    <FormattedMessage
                        defaultMessage="Print Return Label"
                        id="account_order_detail.label.print_return_label"
                    />
                </RegularLink>
                <Text fontSize="xs" lineHeight={'15px'}>
                    <FormattedMessage
                        defaultMessage="Note: You'll only be able to create one return label for this order. If you think you might want to send back more than one item, be sure to include them all now."
                        id="account_order_detail.label.print_return_label_note"
                    />
                </Text>
                <Divider my={6} {...dividerProps} />
            </Stack>
        )
    }
}

export const ReturnAlert = ({orderItems = []}) => {
    const {pendingReturnItems, returnedItems} = orderItems.reduce(
        (acc, item) => {
            if (item?.statusRaw?.toLowerCase() === ORDER_RETURN_RAW_STATUS.PENDING_RETURN)
                acc.pendingReturnItems++
            if (item?.statusRaw?.toLowerCase() === ORDER_RETURN_RAW_STATUS.RETURNED)
                acc.returnedItems++
            return acc
        },
        {pendingReturnItems: 0, returnedItems: 0}
    )

    if (!pendingReturnItems && !returnedItems) return null

    return (
        <>
            {pendingReturnItems > 0 && (
                <Alert variant="warning" mb={2} w="max-content">
                    <FormattedMessage
                        id="account_order_detail.alert.return_pending"
                        defaultMessage="Return pending for {count, plural, one {# item} other {# items}}."
                        values={{count: pendingReturnItems}}
                    />
                </Alert>
            )}

            {returnedItems > 0 && (
                <Alert variant="success" mb={2} w="max-content">
                    <FormattedMessage
                        id="account_order_detail.alert.refund_completed"
                        defaultMessage="Refund completed for {count, plural, one {# item} other {# items}}."
                        values={{count: returnedItems}}
                    />
                </Alert>
            )}
        </>
    )
}

ReturnOrderPolicyAlert.propTypes = {
    returnable: PropTypes.bool,
    containerProps: PropTypes.object
}

ManageOrder.propTypes = {
    returnable: PropTypes.bool,
    returnLabel: PropTypes.string,
    containerProps: PropTypes.object,
    dividerProps: PropTypes.object
}

ReturnAlert.propTypes = {
    orderItems: PropTypes.arrayOf(
        PropTypes.shape({
            statusRaw: PropTypes.string
        })
    )
}
